import { FilterState } from '@/types/filter';
import { UserFilterListItem } from '@/models/user/userFilter';
import { syncSessionStorage, clearSessionStorage } from '@/hooks/useSessionStorage';

export const APPLIED_FILTER_SET_KEY = 'APPLIED_FILTER_SET';

export interface AppliedFilterSet {
  userFilterId: string;
  filterName?: string;
  filterType: string;
  filterCountries: string;
  filterDate: string;
  isDefault?: boolean;
  globalFilters: FilterState;
  localFilters: Record<string, unknown>;
  appliedAt: string;
}

const isAppliedFilterSet = (value: unknown): value is AppliedFilterSet => {
  if (typeof value !== 'object' || value === null) return false;
  if (!('userFilterId' in value) || typeof value.userFilterId !== 'string') return false;
  if (!('globalFilters' in value) || typeof value.globalFilters !== 'object') return false;
  if (!('filterType' in value) || typeof value.filterType !== 'string') return false;
  if (!('appliedAt' in value) || typeof value.appliedAt !== 'string') return false;
  return true;
};

export const AppliedFilterSetStorage = {
  save: (
    filterMetadata: UserFilterListItem,
    globalFilters: FilterState,
    localFilters?: Record<string, unknown>,
  ): void => {
    const entry: AppliedFilterSet = {
      userFilterId: filterMetadata.userFilterId,
      filterName: filterMetadata.filterName,
      filterType: filterMetadata.filterType,
      filterCountries: filterMetadata.filterCountries,
      filterDate: filterMetadata.filterDate,
      isDefault: filterMetadata.isDefault,
      globalFilters,
      localFilters: localFilters ?? {},
      appliedAt: new Date().toISOString(),
    };

    syncSessionStorage(APPLIED_FILTER_SET_KEY, entry);
  },

  get: (): AppliedFilterSet | null => {
    try {
      const item = sessionStorage.getItem(APPLIED_FILTER_SET_KEY);
      if (!item) return null;
      const parsed: unknown = JSON.parse(item);
      return isAppliedFilterSet(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },

  update: (userFilterId: string, changes: Partial<Pick<AppliedFilterSet, 'filterName' | 'isDefault'>>): void => {
    const current = AppliedFilterSetStorage.get();
    if (!current || current.userFilterId !== userFilterId) return;

    syncSessionStorage(APPLIED_FILTER_SET_KEY, { ...current, ...changes });
  },

  clear: (): void => {
    clearSessionStorage(APPLIED_FILTER_SET_KEY);
  },
};
